import React from 'react';
import { Paperclip } from 'lucide-react';
import MessageDeliveryStatus from '../MessageDeliveryStatus';

interface MediaMessageProps {
  message: {
    message_type: string;
    content?: string | null;
    fileName?: string;
    fileSize?: number;
    status?: 'sending' | 'sent' | 'failed';
    created_at?: string | Date;
  };
  mediaUrl?: string;
  tempMediaUrl?: string;
  isUser: boolean;
  isProfileOpen: boolean;
}

export const MediaMessage: React.FC<MediaMessageProps> = ({
  message,
  mediaUrl,
  tempMediaUrl,
  isUser,
  isProfileOpen
}) => {
  const containerClass = isUser ? 'flex justify-end pr-2' : 'flex justify-start pl-2';
  const bubbleClass = `
    relative 
    w-fit 
    max-w-[80%]
    px-4 py-3 
    rounded-xl 
    ${isUser 
      ? 'bg-cyan-500/10 border border-cyan-400/20 text-[var(--text-primary)]' 
      : 'bg-[var(--surface-elevated)] border border-[var(--border-color)] text-[var(--text-primary)]'
    }
    backdrop-blur-sm
    shadow-lg
    mb-2
    hover:border-cyan-400/30
    transition-all
  `;

  const fileAttachmentClass = "flex items-center gap-2 p-2 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)] hover:border-cyan-400/30 transition-colors";
  const fileNameClass = "text-sm text-[var(--text-primary)] hover:text-cyan-400 transition-colors";
  const fileSizeClass = "ml-2 text-[var(--text-secondary)] text-xs";

  const displayUrl = tempMediaUrl || mediaUrl;
  const isLoading = !displayUrl && message.status !== 'failed';
  const hasError = message.status === 'failed';

  const renderTimestampAndStatus = () => {
    let displayTime = null;
    
    if (message.created_at) {
      const messageDate = new Date(message.created_at);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (messageDate.toDateString() === today.toDateString()) {
        displayTime = messageDate.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      } else if (messageDate.toDateString() === yesterday.toDateString()) {
        displayTime = `Yesterday, ${messageDate.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        })}`;
      } else {
        displayTime = messageDate.toLocaleDateString([], { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      }
    }

    return (
      <div className="flex items-center justify-end mt-2 gap-1.5">
        {displayTime && (
          <span className="text-xs text-[var(--text-secondary)]">{displayTime}</span>
        )}
        {message.status && isUser && (
          <MessageDeliveryStatus 
            status={message.status as 'sending' | 'sent' | 'delivered' | 'read'}
            timestamp={new Date(message.created_at || new Date())}
          />
        )}
      </div>
    );
  };

  return (
    <div className={containerClass}>
      <div className={`${bubbleClass} ${isProfileOpen ? 'z-50' : ''}`}>
        <div className="flex flex-col gap-2">
          
          {message.message_type === 'IMAGE' && displayUrl && (
            <img 
              src={displayUrl}
              alt={message.fileName || "Shared image"}
              className="rounded-lg max-w-[300px] border border-[var(--border-color)] hover:border-cyan-400/30 transition-colors"
            />
          )}
          
          {(message.message_type === 'DOCUMENT' || message.message_type === 'FILE') && displayUrl && (
            <div className={fileAttachmentClass}>
              <Paperclip className="h-4 w-4 text-cyan-400" />
              <a 
                href={displayUrl}
                download={message.fileName}
                className={fileNameClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                {message.fileName || 'Download File'}
                {message.fileSize && (
                  <span className={fileSizeClass}>
                    ({(message.fileSize / 1024 / 1024).toFixed(1)}MB)
                  </span>
                )}
              </a>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center gap-2 p-3 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]">
              <div className="h-4 w-4 border-2 border-cyan-400 rounded-full animate-spin" />
              <span className="text-sm text-[var(--text-secondary)]">
                Uploading {message.message_type.toLowerCase()}...
              </span>
            </div>
          )}

          {hasError && (
            <div className="flex items-center gap-2 text-sm text-red-400">
              <span>⚠️</span>
              Failed to upload {message.message_type.toLowerCase()}
            </div>
          )}

          {message.content && (
            <div className="text-sm break-words whitespace-pre-wrap mt-1">
              {message.content}
            </div>
          )}

          {renderTimestampAndStatus()}
        </div>
      </div>
    </div>
  );
}; 